import { getOrCreateStoredBrowserId } from './telemetry-identity';

export type AdminStatusKind = 'status' | 'error';

type SessionState = {
    authenticated: boolean;
};

type AdminSessionOptions = {
    signedOutMessage: string;
    loginFailedMessage: string;
    requestFailedMessage: string;
    setStatus: (message: string, kind?: AdminStatusKind) => void;
    onAuthenticated: () => Promise<void> | void;
    onSignedOut?: () => void;
};

export type AdminSession = {
    initialize: () => Promise<void>;
    request: (url: string, init?: RequestInit) => Promise<Response>;
};

export function createAdminSession(options: AdminSessionOptions): AdminSession {
    const loginPanel = requiredElement<HTMLElement>('login-panel');
    const adminPanel = requiredElement<HTMLElement>('admin-panel');
    const loginForm = requiredElement<HTMLFormElement>('login-form');
    const logoutButton = requiredElement<HTMLButtonElement>('logout-button');
    let antiforgeryToken = '';

    function showAuthenticated(authenticated: boolean): void {
        loginPanel.hidden = authenticated;
        adminPanel.hidden = !authenticated;
        logoutButton.hidden = !authenticated;
        if (!authenticated) options.onSignedOut?.();
    }

    async function refreshAntiforgeryToken(): Promise<void> {
        const response = await fetch('/api/admin/auth/antiforgery', { cache: 'no-store' });
        if (!response.ok) throw new Error(options.requestFailedMessage);
        const payload = await response.json() as { token?: unknown };
        if (typeof payload.token !== 'string') throw new Error(options.requestFailedMessage);
        antiforgeryToken = payload.token;
    }

    async function request(url: string, init: RequestInit = {}): Promise<Response> {
        const headers = new Headers(init.headers);
        headers.set('Accept', 'application/json');
        if (init.method != null && init.method !== 'GET' && init.method !== 'HEAD') {
            headers.set('X-CSRF-TOKEN', antiforgeryToken);
        }
        const response = await fetch(url, { ...init, headers, credentials: 'same-origin', cache: 'no-store' });
        if (response.status === 401) showAuthenticated(false);
        return response;
    }

    loginForm.addEventListener('submit', event => {
        event.preventDefault();
        void (async () => {
            const username = requiredElement<HTMLInputElement>('username').value;
            const password = requiredElement<HTMLInputElement>('password').value;
            // A successful sign-in excludes this browser's public-site visits from the traffic statistics. The ID
            // is stored here when missing so the next public visit reuses it; without storage it is not sent.
            const browserId = getOrCreateStoredBrowserId() ?? undefined;
            const response = await request('/api/admin/auth/login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, password, browserId })
            });
            if (!response.ok) {
                options.setStatus(
                    response.status === 401 ? options.loginFailedMessage : await readAdminError(response, options.requestFailedMessage),
                    'error');
                return;
            }

            loginForm.reset();
            showAuthenticated(true);
            await refreshAntiforgeryToken();
            await options.onAuthenticated();
        })().catch(error => options.setStatus(error instanceof Error ? error.message : options.requestFailedMessage, 'error'));
    });

    logoutButton.addEventListener('click', () => void (async () => {
        const response = await request('/api/admin/auth/logout', { method: 'POST' });
        if (!response.ok) throw new Error(await readAdminError(response, options.requestFailedMessage));
        showAuthenticated(false);
        await refreshAntiforgeryToken();
        options.setStatus(options.signedOutMessage);
    })().catch(error => options.setStatus(error instanceof Error ? error.message : options.requestFailedMessage, 'error')));

    return {
        request,
        async initialize(): Promise<void> {
            await refreshAntiforgeryToken();
            const response = await request('/api/admin/auth/session');
            if (!response.ok) throw new Error(await readAdminError(response, options.requestFailedMessage));
            const session = await response.json() as SessionState;
            showAuthenticated(session.authenticated);
            if (session.authenticated) await options.onAuthenticated();
            else options.setStatus(options.signedOutMessage);
        }
    };
}

export async function readAdminError(response: Response, fallback: string): Promise<string> {
    try {
        const payload = await response.json() as { error?: unknown };
        if (typeof payload.error === 'string') return payload.error;
    } catch {
        // The status fallback below is more useful than a JSON parsing error.
    }
    return `${fallback} (${response.status})`;
}

function requiredElement<T extends HTMLElement>(id: string): T {
    const element = document.getElementById(id);
    if (element == null) throw new Error(`Required element #${id} was not found.`);
    return element as T;
}
